import { StyleShadowRoot } from "@redotech/react-util/style";
import { DeliveryEstimate as DeliveryEstimateType } from "@redotech/redo-model/conversion/conversion-autocheck";
import { PdpPlacementMethod } from "@redotech/redo-model/conversion/storefront-delivery-estimate";
import { IpifyGeoClientProvider } from "@redotech/redo-web/ipify";
import { ModalRootContext } from "@redotech/redo-web/modal-root";
import { assertNever } from "@redotech/util/type";
import { memo } from "react";
import STYLE_MANAGER from "../../style-manager";
import { RedoStorefrontOverlayWrapper } from "../redo-storefront-overlay-wrapper";
import { DateEstimateProvider } from "./date-estimate-context";
import { DeliveryEstimate } from "./delivery-estimate";
import { RedoDeliveryEstimateContainerElement } from "./element";
import { useCartRenderPortals } from "./use-cart-render-portals";
import { useRenderPdps } from "./use-render-pdps";
import { ZipCodeProvider } from "./zip-code-context";

export const DeliveryEstimateInitializer = memo(
  function DeliveryEstimateInitializer({
    deliveryEstimateSettings,
    appBlockContainer,
  }: {
    deliveryEstimateSettings: DeliveryEstimateType;
    appBlockContainer?: RedoDeliveryEstimateContainerElement;
  }) {
    const pdpPortals = useRenderPdps({
      deliveryEstimate: deliveryEstimateSettings,
      fromAppBlock: !!appBlockContainer,
    });
    const cartPortals = useCartRenderPortals({
      deliveryEstimate: deliveryEstimateSettings,
    });

    const ipifyApiKey =
      globalThis.redoStorefront?.env?.IPIFY_API_KEY ??
      process.env.IPIFY_API_KEY;
    if (!ipifyApiKey) {
      console.error("IPIFY_API_KEY is not set");
      return null;
    }

    const storeUrl =
      globalThis.redoStorefront?.storeUrl ?? globalThis.Shopify?.shop;
    if (!storeUrl) {
      console.error("storeUrl is not set");
      return null;
    }

    const allPortals = [...pdpPortals, ...cartPortals];

    if (appBlockContainer || allPortals.length > 0) {
      const deliveryEstimateContent = (
        <IpifyGeoClientProvider apiKey={ipifyApiKey}>
          <ZipCodeProvider>
            <DateEstimateProvider storeUrl={storeUrl}>
              {appBlockContainer ? (
                <StyleShadowRoot
                  shadowRoot={appBlockContainer.root}
                  styleManager={STYLE_MANAGER}
                >
                  <ModalRootContext.Provider
                    value={appBlockContainer.modalContainer}
                  >
                    <DeliveryEstimate />
                  </ModalRootContext.Provider>
                </StyleShadowRoot>
              ) : (
                allPortals.map(({ foundSelector, portal }, index) => (
                  <StyleShadowRoot
                    key={index}
                    shadowRoot={portal.root}
                    styleManager={STYLE_MANAGER}
                  >
                    <ModalRootContext.Provider value={portal.modalContainer}>
                      <DeliveryEstimate />
                    </ModalRootContext.Provider>
                  </StyleShadowRoot>
                ))
              )}
            </DateEstimateProvider>
          </ZipCodeProvider>
        </IpifyGeoClientProvider>
      );

      // If this is being used as an app block (not from theme extension),
      // wrap with the necessary context providers
      if (
        deliveryEstimateSettings.pdpPlacementMethod ===
        PdpPlacementMethod.APP_BLOCK
      ) {
        return (
          <RedoStorefrontOverlayWrapper>
            {deliveryEstimateContent}
          </RedoStorefrontOverlayWrapper>
        );
      } else if (
        deliveryEstimateSettings.pdpPlacementMethod ===
        PdpPlacementMethod.THEME_EXTENSION
      ) {
        // If this is from theme extension, return content directly
        // (context providers are already provided by RedoStorefrontOverlay)
        return deliveryEstimateContent;
      } else {
        assertNever(deliveryEstimateSettings);
      }
    }

    return null;
  },
);
